import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, Group, Text } from '@mantine/core';
import { DataTable, DataTableColumn } from 'mantine-datatable';
import { RemittanceInstruction } from '../../models/remittance-instruction.ts';
import useDownloadRemittanceInstructionReport from '../../hooks/useDownloadRemittanceInstructionReport.ts';
import { notifications } from '@mantine/notifications';
import useVoidRemittanceInstruction from '../../hooks/useVoidRemittanceInstruction.ts';
import { closeAllModals, modals } from '@mantine/modals';

interface RemittanceInstructionsListProps {
  data: RemittanceInstruction[];
  isLoading: boolean;
}

export default function RemittanceInstructionListTable({
  data = [],
  isLoading,
}: RemittanceInstructionsListProps) {
  const { mutate: downloadReport } = useDownloadRemittanceInstructionReport();
  const { mutate: voidRemittanceInstruction } = useVoidRemittanceInstruction();
  const columns: DataTableColumn<RemittanceInstruction>[] = [
    {
      title: '匯款單編號',
      accessor: 'id',
      render: (row) => <Text size="sm">{row.sequenceNumber}</Text>,
    },
    {
      title: '匯款金額',
      accessor: 'amount',
      render: ({ remittanceDetails }) =>
        remittanceDetails
          .reduce((sum, detail) => sum + detail.amount, 0)
          .toLocaleString(),
    },
    {
      title: '產檔時間',
      accessor: 'readyDateTime',
      render: ({ readyDateTime }) => new Date(readyDateTime).toLocaleString(),
    },
    {
      title: '操作',
      accessor: 'actions',
      render: (remittanceInstruction) => (
        <Group>
          <Button
            component={Link}
            to={`/remittance-instructions/${remittanceInstruction.id}`}
            size="xs"
            variant="outline"
          >
            檢視
          </Button>
          <Button
            size="xs"
            variant="outline"
            onClick={() => {
              downloadReport(remittanceInstruction.id, {
                onSuccess: () => {
                  notifications.show({
                    title: '下載成功',
                    message: `匯款單 ${remittanceInstruction.sequenceNumber} 下載成功`,
                    color: 'green',
                  });
                },
              });
            }}
          >
            下載
          </Button>
          <Button
            size="xs"
            color="red"
            variant="outline"
            onClick={() => {
              const modalId = modals.openContextModal({
                modal: 'confirmModal',
                title: `作廢匯款單`,
                innerProps: {
                  modalBody: `確定要作廢匯款單 ${remittanceInstruction.sequenceNumber} 嗎？`,
                  loading: false,
                  onConfirm: () => {
                    modals.updateContextModal({
                      modalId,
                      innerProps: {
                        loading: true,
                      },
                    });
                    voidRemittanceInstruction(remittanceInstruction.id, {
                      onSuccess: () => {
                        notifications.show({
                          title: '成功',
                          message: `匯款單 ${remittanceInstruction.sequenceNumber} 已作廢`,
                          color: 'green',
                        });
                        closeAllModals();
                      },
                      onSettled: () => {
                        modals.updateContextModal({
                          modalId,
                          innerProps: {
                            loading: false,
                          },
                        });
                      },
                    });
                  },
                },
              });
            }}
          >
            作廢
          </Button>
        </Group>
      ),
    },
  ];

  return (
    <Box>
      <DataTable
        idAccessor="id"
        highlightOnHover
        minHeight={150}
        columns={columns}
        records={data}
        verticalSpacing="xs"
        fetching={isLoading}
        noRecordsText="沒有匯款單"
      />
    </Box>
  );
}
